import type { ReactNode } from "react";
import {
    FinancialAccountCombobox,
    type FinancialAccountComboboxProps,
} from "@components/financialAccount/AccountsFilter/FinancialAccountCombobox";
import { FormikField } from "@components/forms/FormikField/FormikField";
import { ariaLabelledbyProps } from "@design-system/formControl/ariaLabelledbyProps";
import type { FieldPath, FieldPathEmptyValue } from "./fieldPath.types";
import { pickChromeProps } from "./kitChromeProps";
import { type KitClearableProps, toClearTarget } from "./kitClearable";
import type {
    KitFieldChromeProps,
    KitFieldControlProps,
} from "./kitFieldProps.types";

type FinancialAccountFieldValue = number | number[] | null | undefined;

/** Paths of `Values` that hold one financial account id. */
export type FinancialAccountFieldPath<Values> = FieldPath<
    Values,
    number | null | undefined,
    number
>;

/** Paths of `Values` that hold financial account ids, for `multiple`. */
export type FinancialAccountFieldMultiplePath<Values> = FieldPath<
    Values,
    number[] | null | undefined,
    number[]
>;

type FinancialAccountControlOptions = Pick<
    FinancialAccountComboboxProps,
    | "accountFilterPredicate"
    | "excludedAccountIds"
    | "isBusinessOnly"
    | "buttonSize"
    | "buttonProps"
    | "hideArrow"
    | "container"
>;

type FinancialAccountFieldBaseProps<Name extends string, Written> = Omit<
    KitFieldControlProps<Name, HTMLButtonElement, Written>,
    "required"
> &
    KitFieldChromeProps &
    FinancialAccountControlOptions;

export type FinancialAccountFieldSingleProps<
    Values,
    Name extends string,
> = FinancialAccountFieldBaseProps<
    Name,
    number | FieldPathEmptyValue<Values, Name>
> &
    KitClearableProps<FieldPathEmptyValue<Values, Name>> & {
        multiple?: false;
    };

export type FinancialAccountFieldMultipleProps<Name extends string> =
    FinancialAccountFieldBaseProps<Name, number[]> & { multiple: true };

export type FinancialAccountFieldProps<Values, Name extends string> =
    | FinancialAccountFieldSingleProps<Values, Name>
    | FinancialAccountFieldMultipleProps<Name>;

/**
 * Financial account picker bound to a typed Formik path: one account id
 * without `multiple`, an id array with it. A single pick is cleared only when
 * `clearable` comes with an `emptyValue` the path allows; otherwise
 * unselecting keeps the last account.
 */
export const FinancialAccountField = <
    Values,
    Name extends
        | FinancialAccountFieldPath<Values>
        | FinancialAccountFieldMultiplePath<Values> =
        | FinancialAccountFieldPath<Values>
        | FinancialAccountFieldMultiplePath<Values>,
>(
    props: FinancialAccountFieldProps<Values, Name>,
): ReactNode => {
    const {
        name,
        ref,
        disabled,
        accountFilterPredicate,
        excludedAccountIds,
        isBusinessOnly,
        buttonSize,
        buttonProps,
        hideArrow,
        container,
    } = props;
    const controlOptions = {
        ref,
        disabled,
        accountFilterPredicate,
        excludedAccountIds,
        isBusinessOnly,
        buttonSize,
        buttonProps: {
            ...buttonProps,
            ...ariaLabelledbyProps(props["aria-labelledby"]),
        },
        hideArrow,
        container,
    };

    return (
        <FormikField<FinancialAccountFieldValue>
            name={name}
            control="button"
            {...pickChromeProps(props)}
        >
            {({ field, helpers, invalid }) => {
                const onBlur = () => void helpers.setTouched(true);

                if (props.multiple === true) {
                    const { onValueChange } = props;
                    return (
                        <FinancialAccountCombobox
                            {...controlOptions}
                            multiple
                            value={
                                Array.isArray(field.value) ? field.value : []
                            }
                            onValueChange={(accountIds) => {
                                void helpers.setValue(accountIds);
                                onValueChange?.(accountIds);
                            }}
                            onBlur={onBlur}
                            invalid={invalid}
                        />
                    );
                }

                const { onValueChange } = props;
                const clearTarget = toClearTarget(props);
                return (
                    <FinancialAccountCombobox
                        {...controlOptions}
                        value={
                            typeof field.value === "number" ? field.value : null
                        }
                        onValueChange={(accountId) => {
                            if (accountId !== null) {
                                void helpers.setValue(accountId);
                                onValueChange?.(accountId);
                            } else if (clearTarget) {
                                void helpers.setValue(clearTarget.emptyValue);
                                onValueChange?.(clearTarget.emptyValue);
                            }
                        }}
                        onBlur={onBlur}
                        invalid={invalid}
                    />
                );
            }}
        </FormikField>
    );
};
